import { ApiError } from "@/shared/api/errors";

export const ATTACHMENT_PUT_TIMEOUT_MS = 15_000;

export type AttachmentPutFailure = {
  error: unknown;
  status: number | null;
  timedOut: boolean;
};

export type AttachmentPutResult =
  | { ok: true }
  | { ok: false; failure: AttachmentPutFailure };

export async function putAttachmentWithTimeout(
  uploadUrl: string,
  file: File,
  mime: string,
  overallSignal: AbortSignal,
  timeoutMs = ATTACHMENT_PUT_TIMEOUT_MS,
): Promise<AttachmentPutResult> {
  if (overallSignal.aborted) {
    return {
      ok: false,
      failure: {
        error: getAbortReason(overallSignal),
        status: null,
        timedOut: false,
      },
    };
  }

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let stallError: DOMException | undefined;
    let settled = false;

    const clearStallTimer = () => {
      if (stallTimer !== undefined) {
        clearTimeout(stallTimer);
        stallTimer = undefined;
      }
    };

    const cleanup = () => {
      clearStallTimer();
      overallSignal.removeEventListener("abort", onOverallAbort);
      xhr.upload.onprogress = null;
      xhr.upload.onload = null;
      xhr.onreadystatechange = null;
      xhr.onload = null;
      xhr.onerror = null;
      xhr.ontimeout = null;
      xhr.onabort = null;
    };

    const finish = (result: AttachmentPutResult) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(result);
    };

    const fail = (error: unknown, timedOut = false) => {
      finish({
        ok: false,
        failure: { error, status: null, timedOut },
      });
    };

    const resetStallTimer = () => {
      if (stallTimer !== undefined) {
        clearTimeout(stallTimer);
      }
      stallTimer = setTimeout(() => {
        stallError = createTimeoutError();
        try {
          xhr.abort();
        } finally {
          fail(stallError, true);
        }
      }, timeoutMs);
    };

    const onOverallAbort = () => {
      const reason = getAbortReason(overallSignal);
      try {
        xhr.abort();
      } finally {
        fail(reason);
      }
    };

    let lastUploadedBytes = 0;
    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (event.loaded > lastUploadedBytes) {
        lastUploadedBytes = event.loaded;
        resetStallTimer();
      }
    };
    xhr.upload.onload = resetStallTimer;
    xhr.onreadystatechange = () => {
      if (
        xhr.readyState < XMLHttpRequest.HEADERS_RECEIVED ||
        xhr.status === 0
      ) {
        return;
      }
      clearStallTimer();
      if (xhr.status < 200 || xhr.status >= 300) {
        finish({
          ok: false,
          failure: {
            error: createHttpError(xhr.status),
            status: xhr.status,
            timedOut: false,
          },
        });
      } else {
        finish({ ok: true });
      }
      xhr.abort();
    };
    xhr.onload = () => {
      if (xhr.status === 0) {
        fail(createNetworkError());
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        finish({ ok: true });
        return;
      }
      finish({
        ok: false,
        failure: {
          error: createHttpError(xhr.status),
          status: xhr.status,
          timedOut: false,
        },
      });
    };
    xhr.onerror = () => fail(createNetworkError());
    xhr.ontimeout = () => fail(createTimeoutError(), true);
    xhr.onabort = () => {
      if (overallSignal.aborted) {
        fail(getAbortReason(overallSignal));
      } else if (stallError) {
        fail(stallError, true);
      } else {
        fail(
          new DOMException(
            "添付ファイルのアップロードが中断されました",
            "AbortError",
          ),
        );
      }
    };
    overallSignal.addEventListener("abort", onOverallAbort, { once: true });

    try {
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", mime);
      if (overallSignal.aborted) {
        onOverallAbort();
        return;
      }
      resetStallTimer();
      xhr.send(file);
    } catch (error) {
      fail(error);
    }
  });
}

export function shouldFallbackAttachmentUpload(
  failure: AttachmentPutFailure,
  overallSignal: AbortSignal,
): boolean {
  if (overallSignal.aborted) {
    return false;
  }
  if (failure.timedOut) {
    return true;
  }
  if (failure.status !== null) {
    return (
      failure.status === 408 ||
      failure.status === 429 ||
      (failure.status >= 500 && failure.status < 600)
    );
  }
  return failure.error instanceof TypeError;
}

function getAbortReason(signal: AbortSignal): unknown {
  return (
    signal.reason ??
    new DOMException("添付ファイルのアップロードが中断されました", "AbortError")
  );
}

function createTimeoutError(): DOMException {
  return new DOMException(
    "添付ファイルのアップロードがタイムアウトしました",
    "TimeoutError",
  );
}

function createNetworkError(): TypeError {
  return new TypeError("Failed to upload attachment");
}

function createHttpError(status: number): ApiError {
  return new ApiError(
    `添付ファイルのアップロードに失敗しました (HTTP ${status})`,
    status,
  );
}
